import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import debounce from "lodash.debounce";
import ContextMenu from "./ContextMenu";
import Pagination from "./Pagination";
import CustomAutocomplete from "./CustomAutocomplete";
import ColumnFilter from "./ColumnFilter";
import "./DataTable.css";
import { DateTime } from "luxon";
import AddressContextMenu from "./AddressContextMenu";
import { Cell } from "./Cell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { toDateTimeLocalValue, fromDateTimeLocalValue, formatDate } from "../../utils/dateUtils";

const DataTable = ({
  columns,
  data,
  onCellUpdate,
  onMassStatusChange,
  onSelectionChange,
  enableRowContextMenu = false,
  getRowContextMenuOptions, 
  onCreateCompany,
  onOpenDetails,
  existingCompanies = [],
  disablePagination = false,
  highlightRowOnEdit = true,
  groupBy = null, // column accessor to group by, e.g. "company_id"
  allPeopleData = [], // All people data for autocomplete duplicate detection
  stickyHeader = false // Enable sticky header
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [localData, setLocalData] = useState(data);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [columnFilters, setColumnFilters] = useState({}); // { columnAccessor: [selectedValues] }
  const tableRef = useRef(null);
  const inputRef = useRef(null);

  // Flatten columns to support grouped headers when looking up colDef by accessor
  const flattenColumns = useCallback((cols) => {
    const out = [];
    (cols || []).forEach((c) => {
      if (c && Array.isArray(c.columns)) {
        out.push(...flattenColumns(c.columns));
      } else if (c) {
        out.push(c);
      }
    });
    return out;
  }, []);
  const flattenedColumns = useMemo(() => flattenColumns(columns), [columns, flattenColumns]);

  // Store column accessor map for filtering
  const [columnAccessorMap, setColumnAccessorMap] = useState({});

  // Apply column filters to data
  const filteredData = useMemo(() => {
    console.log('[DataTable] Applying filters:', columnFilters);
    console.log('[DataTable] localData length:', localData?.length);
    console.log('[DataTable] columnAccessorMap:', columnAccessorMap);
    
    if (Object.keys(columnFilters).length === 0) {
      console.log('[DataTable] No filters active, returning all data');
      return localData;
    }

    const filtered = localData.filter((row, index) => {
      // Check if row passes all active column filters
      const passes = Object.entries(columnFilters).every(([columnId, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) {
          return true; // No filter for this column
        }
        
        // Get the accessor for this column
        const accessor = columnAccessorMap[columnId];
        if (!accessor) {
          console.warn(`[DataTable] No accessor found for column ${columnId}`);
          return true;
        }
        
        // Get the cell value using the accessor
        let cellValue;
        if (typeof accessor === 'function') {
          cellValue = accessor(row);
        } else {
          cellValue = row[accessor];
        }
        
        const cellValueStr = cellValue !== null && cellValue !== undefined ? String(cellValue) : "";
        
        const included = selectedValues.includes(cellValueStr);
        
        if (index < 3) {
          console.log(`[DataTable] Row ${index} - Column ${columnId}:`, {
            cellValue,
            cellValueStr,
            selectedValues,
            included,
            accessor
          });
        }
        
        return included;
      });
      return passes;
    });
    
    console.log('[DataTable] Filtered data length:', filtered.length);
    return filtered;
  }, [localData, columnFilters, columnAccessorMap]);

  // Process data for grouping
  const { groupedData, rowGroups } = useMemo(() => {
    if (!groupBy) {
      return { groupedData: filteredData, rowGroups: {} };
    }

    const groups = {};
    const groupedRows = {};

    // First, identify groups and prepare data
    filteredData.forEach(row => {
      const groupValue = row[groupBy];
      if (!groupValue) return;
      
      if (!groups[groupValue]) {
        groups[groupValue] = [];
      }
      groups[groupValue].push(row);
    });

    // Create grouped data with special markers for group rows
    const result = [];
    Object.entries(groups).forEach(([groupValue, rows]) => {
      // Only create groups if there are multiple rows with the same groupValue
      if (rows.length > 1) {
        // Store the full original row objects with all their data
        const originalRows = [...rows];
        
        // Create a group header row
        const groupRow = {
          ...rows[0],
          id: `group-${groupValue}`, // Ensure group row has a unique ID
          isGroupRow: true,
          groupValue,
          groupSize: rows.length,
          originalRows, // Reference to the full array of original rows
          // Sum total_amount for the group
          total_amount: rows.reduce((sum, row) => sum + (parseFloat(row.total_amount) || 0), 0)
        };
        
        result.push(groupRow);
        groupedRows[groupValue] = originalRows;
        
        // Add child rows if this group is expanded
        if (expandedGroups[groupValue]) {
          rows.forEach(row => {
            result.push({
              ...row,
              isChildRow: true,
              parentGroupValue: groupValue
            });
          });
        }
      } else {
        // Single row with this group value, add as normal
        result.push(rows[0]);
      }
    });

    // Add any rows that don't have a group value
    filteredData.forEach(row => {
      const groupValue = row[groupBy];
      if (!groupValue || (groups[groupValue] && groups[groupValue].length <= 1)) {
        if (!groups[groupValue]) {
          result.push(row);
        }
      }
    });

    return { groupedData: result, rowGroups: groupedRows };
  }, [filteredData, groupBy, expandedGroups]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      if (input.type === "text") {
        setTimeout(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
      }
    }
  }, [editingCell]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setEditingCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle toggling group expansion
  const toggleGroupExpansion = (groupValue) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupValue]: !prev[groupValue]
    }));
  };
  
  const saveChangeDebounced = useRef(
    debounce(async (id, field, value) => {
      try {
        await onCellUpdate(id, field, value);
      } catch (error) {
        console.error("Error updating cell:", error);
      }
    }, 500)
  ).current;

  const handleEditStart = (id, field, value, asChip = false) => {
    setEditingCell({ id, field, asChip });
    setEditedData((prev) => ({
      ...prev,
      [`${id}-${field}`]: value,
    }));
  };

  const handleEditChange = (id, field, value) => {
    // 2a) keep your "draft" state in case you need it elsewhere
    setEditedData(prev => ({
      ...prev,
      [`${id}-${field}`]: value,
    }));
  
    // 2b) optimistically write into the table's data immediately
    setLocalData(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, [field]: value }
          : r
      )
    );
  
    // 2c) kick off the debounced save
    saveChangeDebounced(id, field, value);
  };

  const handleCellClick = (id, field, value) => {
    if (editingCell && (editingCell.id !== id || editingCell.field !== field)) {
      saveChangeDebounced.flush();
    }
    handleEditStart(id, field, value);
  };

  const handleKeyDown = (e, rowIndex, columnIndex, totalRows, totalCols) => {
    const moveToCell = (newRow, newCol) => {
      const columnId = columns[newCol]?.accessor;
      if (columnId !== undefined) {
        setEditingCell({ id: localData[newRow].id, field: columnId });
      }
    };

    switch (e.key) {
      case "Escape":
        setEditingCell(null);
        break;
      case "Enter":
      case "Tab":
        e.preventDefault();
        moveToCell(rowIndex, (columnIndex + 1) % totalCols);
        break;
      case "ArrowRight":
        moveToCell(rowIndex, columnIndex + 1 < totalCols ? columnIndex + 1 : 0);
        break;
      case "ArrowLeft":
        moveToCell(rowIndex, columnIndex > 0 ? columnIndex - 1 : totalCols - 1);
        break;
      case "ArrowDown":
        moveToCell(rowIndex + 1 < totalRows ? rowIndex + 1 : rowIndex, columnIndex);
        break;
      case "ArrowUp":
        moveToCell(rowIndex > 0 ? rowIndex - 1 : rowIndex, columnIndex);
        break;
      default:
        break;
    }
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRows((prev) => {
      const newSelection = { ...prev };
      
      // Handle group row selection
      if (rowId.toString().startsWith('group-')) {
        const groupValue = rowId.replace('group-', '');
        const groupData = rowGroups[groupValue];
        
        if (groupData && groupData.length > 0) {
          // Check if all rows in this group are currently selected
          const allSelected = groupData.every(row => !!prev[row.id]);
          
          if (allSelected) {
            // Deselect all rows in this group
            groupData.forEach(row => {
              delete newSelection[row.id];
            });
            // Also deselect the group row itself
            delete newSelection[rowId];
          } else {
            // Select all rows in this group
            groupData.forEach(row => {
              newSelection[row.id] = true;
            });
            // Also select the group row itself
            newSelection[rowId] = true;
          }
          
          console.log(`Group ${groupValue} toggled. Selected IDs:`, Object.keys(newSelection));
        }
      } else {
        // Regular row selection toggle
        if (newSelection[rowId]) {
          delete newSelection[rowId];
        } else {
          newSelection[rowId] = true;
        }
        
        // Check if this row is part of a group, and update the group row selection status
        if (groupBy) {
          // Find the row's group value
          const row = localData.find(r => r.id === rowId);
          if (row) {
            const groupValue = row[groupBy];
            if (groupValue && rowGroups[groupValue]) {
              const groupId = `group-${groupValue}`;
              const groupData = rowGroups[groupValue];
              
              // Check if all rows in this group are now selected
              const allSelected = groupData.every(r => !!newSelection[r.id]);
              
              // Update the group row's selection accordingly
              if (allSelected) {
                newSelection[groupId] = true;
              } else {
                delete newSelection[groupId];
              }
            }
          }
        }
      }
      
      if (onSelectionChange) {
        // Filter out group rows before sending to onSelectionChange
        const selectedIds = Object.keys(newSelection).filter(id => !id.toString().startsWith('group-'));
        onSelectionChange(selectedIds);
      }
      return newSelection;
    });
  };

  const handleHeaderStatusChange = (newValue) => {
    setLocalData((prev) =>
      prev.map((row) =>
        selectedRows[row.id] ? { ...row, status: newValue } : row
      )
    );
    
    // Get the regular (non-group) selected row IDs
    const selectedRegularRowIds = Object.keys(selectedRows)
      .filter(id => !id.toString().startsWith('group-'));
    
    // Update each selected row
    selectedRegularRowIds.forEach((rowId) => {
      onCellUpdate(rowId, "status", newValue);
    });
    
    if (onMassStatusChange) {
      onMassStatusChange(newValue, selectedRegularRowIds);
    }
  };
  
  // Helper function to get all row IDs (including group and child rows)
  const getAllRowIds = useCallback(() => {
    const allIds = new Set();
    
    // Process the data we're displaying (either localData or groupedData)
    const displayData = groupBy ? groupedData : localData;
    
    displayData.forEach(row => {
      allIds.add(row.id);
      
      // If this is a group row, also add all its child rows
      if (row.isGroupRow && row.originalRows) {
        row.originalRows.forEach(childRow => {
          allIds.add(childRow.id);
        });
      }
    });
    
    return Array.from(allIds);
  }, [groupBy, groupedData, localData]);

  // Helper to check if all rows are selected
  const areAllRowsSelected = useCallback(() => {
    const allIds = getAllRowIds();
    return allIds.length > 0 && allIds.every(id => selectedRows[id]);
  }, [getAllRowIds, selectedRows]);

  const toggleSelectAll = () => {
    if (areAllRowsSelected()) {
      // Deselect all
      setSelectedRows({});
      onSelectionChange && onSelectionChange([]);
    } else {
      // Select all
      const newSelection = {};
      getAllRowIds().forEach(id => {
        newSelection[id] = true;
      });
      setSelectedRows(newSelection);
      
      if (onSelectionChange) {
        // Filter out group rows before sending to onSelectionChange
        const selectedIds = Object.keys(newSelection).filter(id => !id.toString().startsWith('group-'));
        onSelectionChange(selectedIds);
      }
    }
  };

  const initialPageSize = disablePagination ? data.length : 10;

  // Prepare data for the table, using groupedData if groupBy is specified
  const tableData = useMemo(() => {
    return groupBy ? groupedData : filteredData;
  }, [groupBy, groupedData, filteredData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    { 
      columns, 
      data: tableData, 
      initialState: { pageIndex: 0, pageSize: 25 }, 
      autoResetPage: false, 
      autoResetSortBy: false
    },
    useSortBy,
    usePagination
  );

  // Build column accessor map from react-table processed columns
  useEffect(() => {
    if (!headerGroups || headerGroups.length === 0) return;
    
    const map = {};
    headerGroups.forEach(headerGroup => {
      headerGroup.headers.forEach(column => {
        if (column.id && column.accessor) {
          map[column.id] = column.accessor;
        }
      });
    });
    
    console.log('[DataTable] Built column accessor map:', map);
    setColumnAccessorMap(map);
  }, [headerGroups]);

  // Ensure no pagination when disablePagination is true
  useEffect(() => {
    if (disablePagination) {
      setPageSize(tableData.length || 1000000);
    }
  }, [disablePagination, setPageSize, tableData.length]);
  
  const getContrastColor = (color) => {
    if (color[0] === "#") {
      color = color.slice(1);
    }
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? "black" : "white";
  };
  
  const handleCellDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rowId = e.currentTarget.dataset.rowid;
    const field = e.currentTarget.dataset.field;
    // pull the current value from localData
    const value = localData.find(r => String(r.id) === rowId)?.[field];
    handleEditStart(rowId, field, value);
  }, [handleEditStart, localData]);
  
  const darkenColor = (color, amount) => {
    let usePound = false;
    if (color[0] === "#") {
      color = color.slice(1);
      usePound = true;
    }
    let num = parseInt(color, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;

    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));

    let newColor = (r << 16) | (g << 8) | b;
    let newColorStr = newColor.toString(16).padStart(6, "0");
    return (usePound ? "#" : "") + newColorStr;
  };

  const [addressContextMenu, setAddressContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    initialAddress: {},
    rowId: null,
  });

  const handleOpenAddressMenu = (e, initialAddress, rowId) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setAddressContextMenu({
      visible: true,
      x: rect.left,
      y: rect.top,
      initialAddress,
      rowId,
    });
  };
  
  const saveAddress = (newAddress) => {
    onCellUpdate(addressContextMenu.rowId, "address", newAddress);
    setAddressContextMenu(prev => ({ ...prev, visible: false }));
  };
  
  const renderCell = (cell, row, rowIndex, columnIndex) => {
    const column = flattenedColumns.find((col) => col.accessor === cell.column.id);

    // Handle expand/collapse button for group rows
    if (column && column.accessor === "expandCollapse") {
      if (row.original.isGroupRow) {
        return (
          <div className="expand-collapse-container">
            <button 
              className="expand-collapse-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleGroupExpansion(row.original.groupValue);
              }}
              title={expandedGroups[row.original.groupValue] ? "Collapse" : "Expand"}
            >
              <FontAwesomeIcon 
                icon={expandedGroups[row.original.groupValue] ? faChevronDown : faChevronRight} 
              />
            </button>
            <span className="group-count">{row.original.groupSize}</span>
          </div>
        );
      }
      return null;
    }

    if (column?.massSelection) {
      // For group rows, check if all children are selected
      if (row.original.isGroupRow) {
        const groupValue = row.original.groupValue;
        const groupData = rowGroups[groupValue];
        
        // Check if all rows in this group are selected
        const allChildrenSelected = groupData && groupData.length > 0 && 
                                    groupData.every(childRow => selectedRows[childRow.id]);
        
        return (
          <input
            type="checkbox"
            checked={allChildrenSelected}
            onChange={(e) => {
              e.stopPropagation();
              toggleRowSelection(row.original.id);
            }}
          />
        );
      }
      
      // For child rows or regular rows
      return (
        <input
          type="checkbox"
          checked={!!selectedRows[row.original.id]}
          onChange={(e) => {
            e.stopPropagation();
            toggleRowSelection(row.original.id);
          }}
        />
      );
    }

    if (column && column.accessor === "address") {
      const currentAddress = cell.value || {
        address1: row.original.address1,
        address2: row.original.address2,
        city: row.original.city,
        state: row.original.state,
        zip: row.original.zip,
        county: row.original.county,
        country: row.original.country,
      };
    
      const leftParts = [currentAddress.address1, currentAddress.address2, currentAddress.city]
        .filter(Boolean)
        .join(" ");
    
      const rightParts = [currentAddress.state, currentAddress.zip, currentAddress.country || currentAddress.county]
        .filter(Boolean)
        .join(" ");
    
      const addressString = leftParts && rightParts ? `${leftParts}, ${rightParts}` : leftParts || rightParts;
    
      const openAddressMenu = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        setAddressContextMenu({
          visible: true,
          x: rect.left,
          y: rect.top,
          initialAddress: currentAddress,
          rowId: row.original.id,
        });
      };
    
      return (
        <div onContextMenu={openAddressMenu} onDoubleClick={openAddressMenu}>
          <span>{addressString || "Click to add address"}</span>
        </div>
      );
    }
    

    if (column && column.accessor === "company") {
      const isEditingCell =
        editingCell?.id === row.original.id && editingCell?.field === "company";
      const currentValue =
        (editedData[`${row.original.id}-company`] ?? cell.value) || "";
    
      const suggestions = existingCompanies
        .filter((comp) =>
          comp.name.toLowerCase().includes(currentValue.toLowerCase())
        )
        .map((comp) => comp.name);
    
      const isValidCompany = existingCompanies.some(
        (comp) =>
          comp.name.trim().toLowerCase() === currentValue.trim().toLowerCase()
      );
    
      const plusButton = (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCreateCompany(currentValue, row.original);
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "0 4px",
            fontSize: "14px",
            color: "#000",
            lineHeight: 1,
            pointerEvents: "all",
          }}
          title="Add Company"
        >
          +
        </button>
      );
    
      if (isEditingCell) {
        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <CustomAutocomplete
              value={currentValue}
              onChange={(val) => handleEditChange(row.original.id, "company", val)}
              onBlur={() => setEditingCell(null)}
              suggestions={suggestions}
              chipStyle={true}
            />
            {currentValue && !isValidCompany && plusButton}
          </div>
        );
      } else {
        // Display mode.
        if (currentValue) {
          // Check if this column has buttonOnHover property
          const hasButtonOnHover = column?.buttonOnHover && typeof column.buttonOnHover.onClick === "function";
          
          // Function to handle the button click
          const handleButtonClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (onOpenDetails && column.accessor === "company") {
              // For company column, find the company object that matches the company name
              const companyName = currentValue;
              const companyObject = existingCompanies.find(
                company => company.name?.toLowerCase() === companyName?.toLowerCase()
              );
              
              if (companyObject) {
                // Use a special property to indicate we want to open company details
                const companyWrapper = {
                  ...companyObject,
                  __isCompanyDetails: true  // Special flag to indicate this is a company
                };
                // Open details for the company object, not the person
                onOpenDetails(companyWrapper);
              } else if (column.buttonOnHover.onClick) {
                // If company not found, fall back to the column's onClick
                column.buttonOnHover.onClick(row.original);
              }
            } else if (column.buttonOnHover.onClick) {
              // For non-company columns, use the regular onClick handler
              column.buttonOnHover.onClick(row.original);
            }
          };
          
          return isValidCompany ? (
            <div className={hasButtonOnHover ? "button-on-hover-cell" : ""} style={{ width: "100%" }}>
              <div
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onOpenDetails) {
                    onOpenDetails(row.original);
                  } else {
                    setEditingCell({ id: row.original.id, field: "company" });
                  }
                }}
                style={{
                  userSelect: "none",
                  cursor: "pointer",
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: "#e0e0e0",
                  position: "relative",
                  width: "auto",
                }}
              >
                {currentValue}
              </div>
              {hasButtonOnHover && (
                <button 
                  className="button-on-hover" 
                  title={column.buttonOnHover.title || ""} 
                  onClick={handleButtonClick}
                >
                  {column.buttonOnHover.label || "↗"}
                </button>
              )}
            </div>
          ) : (
            <div className={hasButtonOnHover ? "button-on-hover-cell" : ""} style={{ width: "100%" }}>
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <div
                  onClick={() => setEditingCell({ id: row.original.id, field: "company" })}
                  style={{ 
                    cursor: "text",
                    display: "inline-block"
                  }}
                >
                  {currentValue}
                </div>
                {currentValue && !isValidCompany && (
                  <div style={{ display: "inline-block", position: "relative" }}>
                    {plusButton}
                  </div>
                )}
              </div>
              {hasButtonOnHover && (
                <button 
                  className="button-on-hover" 
                  title={column.buttonOnHover.title || ""} 
                  onClick={handleButtonClick}
                >
                  {column.buttonOnHover.label || "↗"}
                </button>
              )}
            </div>
          );
        } else {
          // Empty cell: render blank; single click triggers editing.
          return (
            <div
              onClick={() =>
                setEditingCell({ id: row.original.id, field: "company" })
              }
              style={{ cursor: "text", minHeight: "1.5em" }}
            />
          );
        }
      }
    }
    
    
    if (column?.DropdownOptions) {
      const currentValue =
        editedData[`${row.original.id}-${cell.column.id}`] ?? cell.value;

      const backgroundColor = column.dropdownBackgroundColor
        ? column.dropdownBackgroundColor(currentValue)
        : "#fff";
      const textColor = getContrastColor(backgroundColor);
      const borderColor = darkenColor(backgroundColor, 0.2);

      return (
        <select
          style={{
            backgroundColor,
            color: textColor,
            border: `2px solid ${borderColor}`,
            width: "100%",
            boxSizing: "border-box",
          }}
          value={currentValue}
          onChange={(e) =>
            handleEditChange(row.original.id, cell.column.id, e.target.value)
          }
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) =>
            handleKeyDown(e, rowIndex, columnIndex, page.length, columns.length)
          }
        >
          {column.DropdownOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    

    if (column?.Cell) {
      const cellContent = column.Cell({
        value: cell.value,
        row,
        updateCell: handleEditChange,
        isEditing:
          editingCell?.id === row.original.id && editingCell?.field === cell.column.id,
      });
      
      // Add support for buttonOnHover with custom Cell renderers
      if (column?.buttonOnHover) {
        // Check if we should show the button (either no condition function or it returns true)
        const shouldShow = typeof column.shouldShowButtonOnHover === 'function' 
          ? column.shouldShowButtonOnHover(row)
          : true;
          
        if (shouldShow) {
          const { label, title, onClick } = column.buttonOnHover;
          const handleButtonClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof onClick === "function") {
              onClick(row.original);
            }
          };
          
          return (
            <div className="button-on-hover-cell" style={{ width: "100%" }}>
              <span style={{ display: "inline-block", maxWidth: "calc(100% - 10px)", overflow: "hidden", textOverflow: "ellipsis" }}>
                {cellContent}
              </span>
              <button 
                className="button-on-hover" 
                title={title || ""} 
                onClick={handleButtonClick}
              >
                {label || "↗"}
              </button>
            </div>
          );
        }
      }
      
      return cellContent;
    }
    
    if (column?.autocomplete) {
      const isEditing =
        editingCell?.id === row.original.id && editingCell?.field === cell.column.id;
      const currentValue =
        editedData[`${row.original.id}-${cell.column.id}`] ?? cell.value;
      
      // Determine which data source to use for suggestions
      const isPersonField = ['name', 'email', 'phone'].includes(column.accessor);
      const dataSource = (isPersonField && allPeopleData.length > 0) ? allPeopleData : localData;
      
      const suggestions = Array.from(
        new Set(
          dataSource
            .map((item) => item[column.accessor])
            .filter((val) => val && val !== currentValue)
        )
      );
      
      const autocompleteComponent = isEditing ? (
        <CustomAutocomplete
          value={currentValue}
          onChange={(value) =>
            handleEditChange(row.original.id, cell.column.id, value)
          }
          onBlur={() => setEditingCell(null)}
          suggestions={suggestions}
          peopleData={isPersonField ? allPeopleData : []}
          fieldType={isPersonField ? column.accessor : null}
          currentValue={currentValue}
        />
      ) : (
        <span onDoubleClick={() => handleEditStart(row.original.id, cell.column.id, cell.value)}>
          {cell.value}
        </span>
      );
      
      // If buttonOnHover is also set, wrap the autocomplete component with a hover button.
      if (column?.buttonOnHover) {
        // Check if we should show the button (either no condition function or it returns true)
        const shouldShow = typeof column.shouldShowButtonOnHover === 'function' 
          ? column.shouldShowButtonOnHover(row)
          : true;
          
        if (shouldShow) {
          const { label, title, onClick } = column.buttonOnHover;
          const handleButtonClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (onOpenDetails && column.accessor === "company") {
              // For company column, find the company object that matches the company name
              const companyName = cell.value;
              const companyObject = existingCompanies.find(
                company => company.name?.toLowerCase() === companyName?.toLowerCase()
              );
              
              if (companyObject) {
                // Use a special property to indicate we want to open company details
                const companyWrapper = {
                  ...companyObject,
                  __isCompanyDetails: true  // Special flag to indicate this is a company
                };
                // Open details for the company object, not the person
                onOpenDetails(companyWrapper);
              } else if (column.buttonOnHover.onClick) {
                // If company not found, fall back to the column's onClick
                column.buttonOnHover.onClick(row.original);
              }
            } else if (column.buttonOnHover.onClick) {
              // For non-company columns, use the regular onClick handler
              column.buttonOnHover.onClick(row.original);
            }
          };
          
          return (
            <div className="button-on-hover-cell" style={{ width: "100%" }}>
              <span style={{ display: "inline-block", maxWidth: "calc(100% - 10px)", overflow: "hidden", textOverflow: "ellipsis" }}>
                {autocompleteComponent}
              </span>
              {column?.buttonOnHover && (
                <button
                  className="button-on-hover"
                  title={title || ""}
                  onClick={handleButtonClick}
                >
                  {label || "↗"}
                </button>
              )}
            </div>
          );
        }
      }
      
      return autocompleteComponent;
    }
    
    // Existing buttonOnHover logic for non-autocomplete columns.
    if (column?.buttonOnHover) {
      // Check if we should show the button (either no condition function or it returns true)
      const shouldShow = typeof column.shouldShowButtonOnHover === 'function' 
        ? column.shouldShowButtonOnHover(row)
        : true;
        
      if (shouldShow) {
        const { label, title, onClick } = column.buttonOnHover;
        const handleButtonClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (onOpenDetails && column.accessor === "company") {
            // For company column, find the company object that matches the company name
            const companyName = cell.value;
            const companyObject = existingCompanies.find(
              company => company.name?.toLowerCase() === companyName?.toLowerCase()
            );
            
            if (companyObject) {
              // Use a special property to indicate we want to open company details
              const companyWrapper = {
                ...companyObject,
                __isCompanyDetails: true  // Special flag to indicate this is a company
              };
              // Open details for the company object, not the person
              onOpenDetails(companyWrapper);
            } else if (column.buttonOnHover.onClick) {
              // If company not found, fall back to the column's onClick
              column.buttonOnHover.onClick(row.original);
            }
          } else if (column.buttonOnHover.onClick) {
            // For non-company columns, use the regular onClick handler
            column.buttonOnHover.onClick(row.original);
          }
        };
        return (
          <div className="button-on-hover-cell" style={{ width: "100%" }}>
            <span style={{ display: "inline-block", maxWidth: "calc(100% - 10px)", overflow: "hidden", textOverflow: "ellipsis" }}>
              {cell.value}
            </span>
            {column?.buttonOnHover && (
              <button 
                className="button-on-hover" 
                title={title || ""} 
                onClick={handleButtonClick}
              >
                {label || "↗"}
              </button>
            )}
          </div>
        );
      }
    }
    
    if (column?.chips) {
      let chips = [];
      if (Array.isArray(cell.value)) {
        chips = cell.value;
      } else if (typeof cell.value === "string") {
        chips = cell.value.split(",").map((chip) => chip.trim());
      }

      return (
        <div
          className="chip-container"
          onDoubleClick={() =>
            handleEditStart(row.original.id, cell.column.id, cell.value)
          }
        >
          {chips.map((chip, index) => {
            let chipColor;
            if (column.chipColor) {
              chipColor =
                typeof column.chipColor === "function"
                  ? column.chipColor(chip, row.original)
                  : column.chipColor;
            }
            chipColor = chipColor || "#e0e0e0";
            return (
              <span
                key={index}
                className="chip"
                style={{
                  backgroundColor: chipColor,
                  color: getContrastColor(chipColor),
                }}
              >
                {chip}
              </span>
            );
          })}
        </div>
      );
    }

    if (column?.datePicker) {
      const isEditing =
        editingCell?.id === row.original.id && editingCell?.field === cell.column.id;
    
      // Convert the UTC date to a local datetime value for the input
      const localDateTimeString = cell.value ? toDateTimeLocalValue(cell.value) : "";
      
      // Format the date for display with timezone info
      const displayDateTimeString = cell.value ? formatDate(cell.value) : "";
    
      if (isEditing) {
        return (
          <input
            ref={inputRef}
            type="datetime-local"
            value={localDateTimeString}
            onChange={(e) => {
              // Convert the local input value to UTC for storage
              const isoDateTime = fromDateTimeLocalValue(e.target.value);
              handleEditChange(row.original.id, cell.column.id, isoDateTime);
            }}
            onBlur={() => setEditingCell(null)}
            onKeyDown={(e) =>
              handleKeyDown(e, rowIndex, columnIndex, page.length, columns.length)
            }
          />
        );
      }
      return (
        <span onDoubleClick={() => handleEditStart(row.original.id, cell.column.id, cell.value)}>
          {displayDateTimeString}
        </span>
      );
    }
    

    if (column?.chipDropdown) {
      const currentValue =
        editedData[`${row.original.id}-${cell.column.id}`] ?? cell.value;
      let chipColor;
      if (column.chipDropdownChipColor) {
        chipColor =
          typeof column.chipDropdownChipColor === "function"
            ? column.chipDropdownChipColor(row.original)
            : column.chipDropdownChipColor;
      }
      chipColor = chipColor || "#e0e0e0";
      const textColor = getContrastColor(chipColor);
      return (
        <select
          style={{
            backgroundColor: chipColor,
            color: textColor,
            border: "none",
            borderRadius: "12px",
            padding: "2px 8px",
            fontSize: "12px",
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
          }}
          value={currentValue}
          onChange={(e) =>
            handleEditChange(row.original.id, cell.column.id, e.target.value)
          }
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) =>
            handleKeyDown(e, rowIndex, columnIndex, page.length, columns.length)
          }
        >
          {column.chipDropdownOptions.map((option) => (
            <option
              key={option[column.chipDropdownValueField]}
              value={option[column.chipDropdownValueField]}
            >
              {column.chipDropdownDisplay
                ? column.chipDropdownDisplay(option)
                : option[column.chipDropdownValueField]}
            </option>
          ))}
        </select>
      );
    }

    const draftKey = `${row.original.id}-${cell.column.id}`;
    const isEditing =
      editingCell?.id === row.original.id &&
      editingCell?.field === cell.column.id;
    const displayValue = isEditing
      ? (editedData[draftKey] ?? cell.value)
      : cell.value;

    // render an <input> when editing…
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={e =>
            handleEditChange(row.original.id, cell.column.id, e.target.value)
          }
          onBlur={() => setEditingCell(null)}
          onKeyDown={e =>
            handleKeyDown(e, rowIndex, columnIndex, page.length, columns.length)
          }
        />
      );
    }

    return (
      <span
        className="editable-cell"
        data-rowid={row.original.id}
        data-field={cell.column.id}
        onDoubleClick={handleCellDoubleClick}
      >
        {cell.value}
      </span>
    );
  };


return (
  <div className="data-table" ref={tableRef}>
    <div className="data-table-container">
      {/* main scrollable table */}
      <div className={`scroll-container ${stickyHeader ? 'sticky-header-enabled' : ''}`}>
        <table {...getTableProps()} className="table">
          {/* ---------- TABLE HEAD ---------- */}
          <thead className={stickyHeader ? 'sticky-header' : ''}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => {
                  const baseProps = column.getHeaderProps();
                  const sortProps = (
                    column.massSelection
                      ? {}
                      : (column.id === "status" && Object.keys(selectedRows).length > 1)
                        ? {}
                        : column.getSortByToggleProps()
                  );
                  return (
                  <th
                    key={column.id}
                    {...baseProps}
                    {...sortProps}
                    style={{ width: column.width ? `${column.width}px` : "auto" }}
                  >
                    {column.massSelection ? (
                      <input
                        type="checkbox"
                        checked={areAllRowsSelected()}
                        onChange={toggleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : column.id === "status" &&
                      Object.keys(selectedRows).length > 1 ? (
                      /* mass status-change dropdown in header */
                      (() => {
                        const defaultValue = column.DropdownOptions[0];
                        const backgroundColor = column.dropdownBackgroundColor
                          ? column.dropdownBackgroundColor(defaultValue)
                          : "#fff";
                        const textColor = getContrastColor(backgroundColor);
                        const borderColor = darkenColor(backgroundColor, 0.2);
                        return (
                          <select
                            style={{
                              backgroundColor,
                              color: textColor,
                              border: `2px solid ${borderColor}`,
                              width: "100%",
                              boxSizing: "border-box",
                            }}
                            onChange={(e) =>
                              handleHeaderStatusChange(e.target.value)
                            }
                          >
                            {column.DropdownOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        );
                      })()
                    ) : (
                      <div 
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}
                        onClick={(e) => {
                          // Prevent sort when clicking the filter area
                          if (e.target.closest('.column-filter')) {
                            e.stopPropagation();
                          }
                        }}
                      >
                        <span style={{ flex: 1 }}>{column.render("Header")}</span>
                        {!column.massSelection && !column.disableFilter && (column.accessor || column.id) && (
                          <ColumnFilter
                            column={column}
                            data={localData}
                            onFilterChange={(columnId, values) => {
                              console.log('[DataTable] Filter changed for', columnId, ':', values);
                              setColumnFilters(prev => {
                                if (values.length === 0) {
                                  const newFilters = { ...prev };
                                  delete newFilters[columnId];
                                  console.log('[DataTable] Removing filter, new filters:', newFilters);
                                  return newFilters;
                                }
                                const newFilters = { ...prev, [columnId]: values };
                                console.log('[DataTable] Adding filter, new filters:', newFilters);
                                return newFilters;
                              });
                            }}
                            currentFilters={columnFilters[column.id] || []}
                          />
                        )}
                      </div>
                    )}
                  </th>
                );})}
              </tr>
            ))}
          </thead>

          {/* ---------- TABLE BODY ---------- */}
          <tbody {...getTableBodyProps()}>
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const isEditingRow =
                highlightRowOnEdit && editingCell?.id === row.original.id;
              const isGroupRow = row.original.isGroupRow;
              const isChildRow = row.original.isChildRow;
              const isTotalsRow = row.original.isTotals;
              const isWeeklyTotalRow = row.original.isWeeklyTotal;
              
              return (
                <tr
                  {...row.getRowProps()}
                  key={rowIndex}
                  className={`${isEditingRow ? "editing-row" : ""} 
                            ${isGroupRow ? "group-row" : ""} 
                            ${isChildRow ? "child-row" : ""}
                            ${isTotalsRow ? "totals-row" : ""}
                            ${isWeeklyTotalRow ? "weekly-total-row" : ""}`}
                  onContextMenu={(e) => {
                    if (enableRowContextMenu && getRowContextMenuOptions) {
                      e.preventDefault();
                      const options = getRowContextMenuOptions(row.original);
                      setContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                        options,
                        row: row.original,
                      });
                    }
                  }}
                >
                  {row.cells.map((cell, columnIndex) => {
                    const colDef = flattenedColumns.find((c) => c.accessor === cell.column.id);
                    
                    // For group rows, handle special rendering of certain columns
                    if (row.original.isGroupRow && colDef) {
                      // If it's the expandCollapse column, render the toggle button
                      if (colDef.accessor === "expandCollapse") {
                        return (
                          <td
                            {...cell.getCellProps()}
                            key={cell.column.id}
                            style={{ width: colDef?.width || "auto" }}
                          >
                            <div className="expand-collapse-container">
                              <button 
                                className="expand-collapse-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroupExpansion(row.original.groupValue);
                                }}
                                title={expandedGroups[row.original.groupValue] ? "Collapse" : "Expand"}
                              >
                                <FontAwesomeIcon 
                                  icon={expandedGroups[row.original.groupValue] ? faChevronDown : faChevronRight} 
                                />
                              </button>
                              <span className="group-count">{row.original.groupSize}</span>
                            </div>
                          </td>
                        );
                      }
                    }
                    
                    return (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        style={{ width: colDef?.width || "auto" }}
                        tabIndex={0}
                        onClick={
                          colDef?.massSelection || row.original.isGroupRow
                            ? undefined
                            : () =>
                                handleCellClick(
                                  row.original.id,
                                  cell.column.id,
                                  cell.value
                                )
                        }
                        onDoubleClick={
                          colDef?.massSelection || row.original.isGroupRow
                            ? undefined
                            : () =>
                                handleEditStart(
                                  row.original.id,
                                  cell.column.id,
                                  cell.value
                                )
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(
                            e,
                            rowIndex,
                            columnIndex,
                            page.length,
                            columns.length
                          )
                        }
                      >
                        {renderCell(cell, row, rowIndex, columnIndex)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------- PAGINATION ---------- */}
      {!disablePagination && (
        <Pagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          setPageSize={setPageSize}
          localData={localData}
        />
      )}

      {/* ---------- CONTEXT MENUS ---------- */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenu.options}
          onClose={() => setContextMenu(null)}
        />
      )}
      {addressContextMenu.visible && (
        <AddressContextMenu
          x={addressContextMenu.x}
          y={addressContextMenu.y}
          initialAddress={addressContextMenu.initialAddress}
          onSave={saveAddress}
          onClose={() =>
            setAddressContextMenu({
              ...addressContextMenu,
              visible: false,
            })
          }
        />
      )}
    </div>
  </div>
);

};

export default DataTable;